/**
 * First-login and changed-version terms acceptance (#3014).
 *
 * Wraps the signed-in app. When the API says documents are pending, a dialog
 * that cannot be dismissed asks the user to accept the terms and privacy notice
 * in force; accepting records who, which versions and when on the server.
 *
 * What the gate does with each answer from the API:
 *
 * - `unconfigured` (OceanSpace has not set the documents for this deployment):
 *   the app renders as before. This is off by design, not a failure.
 * - `active` with something pending: the dialog.
 * - `invalid` (documents were configured but the configuration is faulty): a
 *   blocking notice with no way to accept. A faulty configuration must not
 *   quietly let users past a document that was meant to be required.
 * - no answer yet: a loading state, never the app. The gate fails closed: the
 *   app is not shown on the assumption that nothing is pending.
 * - the status request itself failing (a network error, an API without the
 *   route): an error state with a retry and a way to sign out, not the app.
 *   An answer that was already received keeps applying while a background
 *   refetch fails.
 *
 * This is a UI gate only. The API does not refuse other requests from a user
 * who has not accepted (a product decision, see docs/operations/legal-consent.md).
 *
 * Two things the dialog guarantees. The tick is bound to the exact set of
 * document versions on screen, so a background refetch that swaps a version
 * clears it and the user must read and tick again. And what is submitted is
 * that same set, taken from the render the user ticked in. The cache and the
 * dialog state are scoped to the signed-in user and tenant, so a second login in
 * the same tab never inherits the first one's answer or tick.
 */

import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Link,
  List,
  ListItem,
  Typography,
} from '@mui/material';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { api, ApiError } from '@/lib/api';
import { useSignOut } from '@/hooks/useAuth';
import { useAuthStore } from '@/stores/authStore';

export interface LegalConsentDocument {
  type: 'terms' | 'privacy';
  version: string;
  url: string;
  acceptedAt: string | null;
}

export interface LegalConsentStatus {
  status: 'unconfigured' | 'active' | 'invalid';
  enforced: boolean;
  pending: boolean;
  documents: LegalConsentDocument[];
}

/**
 * Scoped to the signed-in user and tenant. Logout empties the whole cache, but a
 * session that ends by a failed token refresh does not, and the next login would
 * otherwise start from the previous person's answer.
 */
export function legalConsentStatusKey(tenantId: string, userId: string) {
  return ['legal-consent', 'status', tenantId, userId] as const;
}

export function useLegalConsentStatus(tenantId: string, userId: string) {
  return useQuery({
    queryKey: legalConsentStatusKey(tenantId, userId),
    queryFn: () => api.get<LegalConsentStatus>('/legal-consent/status'),
    // One answer decides: a failure shows the gate's error state with a retry
    // button straight away instead of retrying behind a spinner.
    retry: false,
    staleTime: 5 * 60_000,
  });
}

/** Full-page layout of the loading and error states that stand in for the app. */
const GATE_STATE_SX = {
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 2,
  px: 2,
} as const;

/** Defence in depth: the server validates https, the link still checks. */
function isHttps(url: string): boolean {
  return url.startsWith('https://');
}

/** Identity of a set of documents: changes when any version or the list does. */
function documentSetKey(documents: readonly LegalConsentDocument[]): string {
  return documents.map((doc) => `${doc.type}@${doc.version}`).join('|');
}

export function LegalConsentGate({ children }: { children: ReactNode }) {
  const user = useAuthStore((state) => state.user);
  if (!user) return <>{children}</>;
  // Keyed on user and tenant: a different person or workshop is a fresh gate
  // with no tick, no error and no mutation state carried over.
  return (
    <ScopedLegalConsentGate key={`${user.tenantId}:${user.id}`} tenantId={user.tenantId} userId={user.id}>
      {children}
    </ScopedLegalConsentGate>
  );
}

function ScopedLegalConsentGate({
  tenantId,
  userId,
  children,
}: {
  tenantId: string;
  userId: string;
  children: ReactNode;
}) {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();
  const signOut = useSignOut();
  const statusKey = legalConsentStatusKey(tenantId, userId);
  const status = useLegalConsentStatus(tenantId, userId);
  // The set the user ticked for, not a bare boolean: a boolean outlives the
  // documents it was given for when a refetch replaces them.
  const [agreedFor, setAgreedFor] = useState<string | null>(null);
  const [staleVersion, setStaleVersion] = useState(false);

  const documents = status.data?.documents ?? [];
  const setKey = documentSetKey(documents);
  const agreed = agreedFor !== null && agreedFor === setKey;

  const accept = useMutation({
    mutationFn: (seen: readonly LegalConsentDocument[]) =>
      api.post('/legal-consent/accept', {
        documents: seen.map(({ type, version }) => ({ type, version })),
      }),
    onSuccess: () => {
      setStaleVersion(false);
      void queryClient.invalidateQueries({ queryKey: statusKey });
    },
    onError: (error) => {
      // The versions changed under an open page: show the new ones, and make
      // the user read and tick again rather than accept what they did not see.
      if (error instanceof ApiError && error.status === 409) {
        setStaleVersion(true);
        setAgreedFor(null);
        void queryClient.invalidateQueries({ queryKey: statusKey });
      }
    },
  });

  const answer = status.data;
  // Fail closed: without an answer the app stays hidden (#3014 review).
  if (answer === undefined) {
    if (status.isError) {
      return (
        <Box sx={GATE_STATE_SX}>
          <Alert severity="error" sx={{ maxWidth: 480 }}>
            {t('legalConsent.statusFailed')}
          </Alert>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={() => void signOut()}>{t('logout')}</Button>
            <Button
              variant="contained"
              disabled={status.isFetching}
              onClick={() => void status.refetch()}
            >
              {t('legalConsent.retry')}
            </Button>
          </Box>
        </Box>
      );
    }
    return (
      <Box sx={GATE_STATE_SX} role="status" aria-live="polite">
        <CircularProgress size={32} aria-hidden />
        <Typography variant="body2" color="text.secondary">
          {t('legalConsent.checking')}
        </Typography>
      </Box>
    );
  }
  if (!answer.enforced || !answer.pending) return <>{children}</>;

  if (answer.status === 'invalid') {
    return (
      <>
        {children}
        <ResponsiveDialog
          open
          fullWidth
          maxWidth="sm"
          disableEscapeKeyDown
          mobileTitle={t('legalConsent.title')}
          aria-labelledby="legal-consent-title"
        >
          <DialogTitle id="legal-consent-title">{t('legalConsent.title')}</DialogTitle>
          <DialogContent>
            <Alert severity="error">{t('legalConsent.unavailable')}</Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => void signOut()}>{t('logout')}</Button>
          </DialogActions>
        </ResponsiveDialog>
      </>
    );
  }

  // Re-asking after a version change reads differently from the first time.
  const returning = documents.some((doc) => doc.acceptedAt !== null);

  return (
    <>
      {children}
      <ResponsiveDialog
        open
        fullWidth
        maxWidth="sm"
        disableEscapeKeyDown
        mobileTitle={t('legalConsent.title')}
        aria-labelledby="legal-consent-title"
      >
        <DialogTitle id="legal-consent-title">{t('legalConsent.title')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {returning ? t('legalConsent.introChanged') : t('legalConsent.intro')}
          </Typography>
          <List dense disablePadding>
            {documents.map((doc) => (
              <ListItem key={doc.type} disableGutters>
                {isHttps(doc.url) ? (
                  <Link href={doc.url} target="_blank" rel="noopener noreferrer">
                    {t(`legalConsent.documents.${doc.type}`)}
                  </Link>
                ) : (
                  <Typography>{t(`legalConsent.documents.${doc.type}`)}</Typography>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  {t('legalConsent.version', { version: doc.version })}
                </Typography>
              </ListItem>
            ))}
          </List>
          <FormControlLabel
            sx={{ mt: 1 }}
            control={
              <Checkbox
                checked={agreed}
                onChange={(event) => setAgreedFor(event.target.checked ? setKey : null)}
              />
            }
            label={t('legalConsent.agree')}
          />
          {staleVersion && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              {t('legalConsent.versionChanged')}
            </Alert>
          )}
          {accept.isError && !staleVersion && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {t('legalConsent.acceptFailed')}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => void signOut()} disabled={accept.isPending}>
            {t('logout')}
          </Button>
          <Button
            variant="contained"
            disabled={!agreed || accept.isPending}
            onClick={() => accept.mutate(documents)}
          >
            {t('legalConsent.accept')}
          </Button>
        </DialogActions>
      </ResponsiveDialog>
    </>
  );
}
